import { PlusIconSVG } from "@/assets/svg";
import AppLayout from "@/components/layout/AppLayout";
import Modal from "@/components/Modal";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import EmailFilters from "./components/EmailFilters";
import EmailTable from "./components/EmailTable";
import NewEmailForm, { NewEmailFormData } from "./components/NewEmailForm";
import ViewEmailForm, { ViewEmailData } from "./components/ViewEmailForm";
import ResendFailedEmailForm, { ResendEmailData } from "./components/ResendFailedEmailForm";
import moment from "moment";
import { getProjectFilterListService } from "@/service/project.service";
import { getClientFilterService } from "@/service/client.service";
import { getScenariosListService, sendProjectEmailService, getEmailViewService } from "@/service/email.service";
import { successToast, errorToast } from "@/utils/toast";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmails,
  selectEmailRows,
  selectEmailPagination,
} from "@/store/slices/emailSlice";

//

export default function Email() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("All clients");
  const [projectFilter, setProjectFilter] = useState("All projects");
  const [scenarioFilter, setScenarioFilter] = useState("All scenarios");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const [isNewEmailOpen, setIsNewEmailOpen] = useState(false);
  const [isViewEmailOpen, setIsViewEmailOpen] = useState(false);
  const [viewEmailData, setViewEmailData] = useState<ViewEmailData | null>(null);
  const [isResendOpen, setIsResendOpen] = useState(false);
  const [resendData, setResendData] = useState<ResendEmailData | null>(null);
  const [resendEmailId, setResendEmailId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<{ clientId: string; projectId: string } | null>(null);
  const payloadHandledRef = useRef(false);

  const [projectOptions, setProjectOptions] = useState<
    { value: string; label: string }[]
  >([{ value: "All projects", label: "All projects" }]);
  const [clientOptions, setClientOptions] = useState<
    { value: string; label: string }[]
  >([{ value: "All clients", label: "All clients" }]);
  const [scenarioOptions, setScenarioOptions] = useState<
    { value: string; label: string }[]
  >([{ value: "All scenarios", label: "All scenarios" }]);

  // Date range filter (YYYY-MM-DD strings like other pages)
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });
  const [tempDateRange, setTempDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const rows = useSelector(selectEmailRows);
  const pagination = useSelector(selectEmailPagination);

  const statusOptions = [
    { value: "All statuses", label: "All statuses" },
    { value: "Sent", label: "Sent" },
    { value: "Failed", label: "Failed" },
  ];

  const handleAddProject = () => {
    setIsNewEmailOpen(true);
  };

  const handleResendSuccessAndRefresh = () => {
    successToast("Email resent successfully", "");
    setIsResendOpen(false);
    const from = dateRange.startDate
      ? moment(dateRange.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")
      : undefined;
    const to = dateRange.endDate
      ? moment(dateRange.endDate, "YYYY-MM-DD").format("DD-MM-YYYY")
      : undefined;
    const listPayload: any = {
      page: currentPage,
      pageSize,
    };
    if (search) listPayload.search = search;
    if (projectFilter !== "All projects") listPayload.projectId = projectFilter;
    if (clientFilter !== "All clients") listPayload.clientId = clientFilter;
    if (scenarioFilter !== "All scenarios")
      listPayload.scenario = scenarioFilter.toLowerCase();
    if (statusFilter !== "All statuses")
      listPayload.status = statusFilter.toLowerCase();
    if (from) listPayload.fromDate = from;
    if (to) listPayload.toDate = to;
    // @ts-ignore thunk typing from slice
    dispatch(fetchEmails(listPayload));
  };

  const handleViewEmailsFromForm = (rawDate: string) => {
    // Normalize to YYYY-MM-DD
    const m = moment(rawDate, [
      "YYYY-MM-DD",
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      "DD-MM-YYYY",
      "MM-DD-YYYY",
    ], true);
    const ymd = m.isValid() ? m.format("YYYY-MM-DD") : rawDate; // fallback if already proper
    setDateRange({ startDate: ymd, endDate: ymd });
    setTempDateRange({ startDate: ymd, endDate: ymd });
    setCurrentPage(1);
    setIsNewEmailOpen(false);
  };

  const handleViewEmail = async (emailId: string) => {
    try {
      const { data } = await getEmailViewService(emailId);
      const item: any = data.data;

      setViewEmailData({
        clientName: item?.client?.name || "",
        projectName: item?.project?.name || "",
        scenario: item?.scenario ? item.scenario.charAt(0).toUpperCase() + item.scenario.slice(1) : "",
        sentDate: item?.send_date || "",
        sentTime: item?.send_time || "",
        to: item?.to || [],
        cc: item?.cc || [],
        bcc: item?.bcc || [],
        subject: item?.subject || "",
        contentHtml: item?.body || "",
      });
      setIsViewEmailOpen(true);
    } catch (e: any) {
      errorToast(e?.response?.data?.message || "Failed to fetch email details", "");
    }
  };

  const handleResendFailedEmail = async (emailId: string) => {
    try {
      const { data } = await getEmailViewService(emailId);
      const item: any = data.data;
      const proj = item?.project || {};

      setResendEmailId(emailId);
      setResendData({
        reason: item?.error || null,
        clientId: item?.client?.id || "",
        clientName: item?.client?.name || "",
        projectId: proj?.id || "",
        projectName: proj?.name || "",
        scenario: item?.scenario || "",
        lastSendDate: item?.send_date || null,
        end_date: proj?.end_date || undefined,
        esti_hrs: proj?.esti_hrs,
        util_hrs: proj?.util_hrs,
        remi_hrs: proj?.remi_hrs,
        to: item?.to || [],
        cc: item?.cc || [],
        bcc: item?.bcc || [],
        subject: item?.subject || "",
        bodyHtml: item?.body || "",
      });
      setIsResendOpen(true);
    } catch (e: any) {
      errorToast(e?.response?.data?.message || "Failed to fetch email details", "");
    }
  };

  const handleSendEmail = async (data: NewEmailFormData) => {
    try {
      const toArr = data.to ? [data.to] : [];
      const ccArr = (data.cc || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const bccArr = (data.bcc || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const normalizeEndDate = (v?: string) => {
        if (!v) return "";
        const onlyDigits = v.replace(/\D/g, "");
        if (/^\d{8}$/.test(onlyDigits)) return onlyDigits; // already DDMMYYYY or similar
        // Try DD/MM/YYYY
        const m = /^([0-3]?\d)[/-]([0-1]?\d)[/-](\d{4})$/.exec(v);
        if (m) return `${m[1].padStart(2, "0")}${m[2].padStart(2, "0")}${m[3]}`;
        return onlyDigits;
      };

      const payload = {
        project_id: data.project,
        client_id: data.client_user_id || data.client,
        scenario: data.scenario,
        esti_hrs: Number(data.esti_hrs ?? 0) || 0,
        util_hrs: Number(data.util_hrs ?? 0) || 0,
        remi_hrs: Number(data.remi_hrs ?? 0) || 0,
        end_date: normalizeEndDate(data.end_date),
        to: toArr,
        cc: ccArr,
        bcc: bccArr,
        subject: data.subject,
        body: data.content,
        org_id: data.org_id,
        action: "send_now" as const,
      };

      await sendProjectEmailService(data.project, payload);
      successToast("Email sent successfully", "");
      setIsNewEmailOpen(false);

      // Refresh list using current filters
      const from = dateRange.startDate
        ? moment(dateRange.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")
        : undefined;
      const to = dateRange.endDate
        ? moment(dateRange.endDate, "YYYY-MM-DD").format("DD-MM-YYYY")
        : undefined;

      const listPayload: any = {
        page: currentPage,
        pageSize,
      };
      if (search) listPayload.search = search;
      if (projectFilter !== "All projects") listPayload.projectId = projectFilter;
      if (clientFilter !== "All clients") listPayload.clientId = clientFilter;
      if (scenarioFilter !== "All scenarios")
        listPayload.scenario = scenarioFilter.toLowerCase();
      if (statusFilter !== "All statuses")
        listPayload.status = statusFilter.toLowerCase();
      if (from) listPayload.fromDate = from;
      if (to) listPayload.toDate = to;

      // @ts-ignore thunk typing from slice
      dispatch(fetchEmails(listPayload));
    } catch (e: any) {
      errorToast(e?.response?.data?.message || "Failed to send email", "");
    }
  };

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // serialized calls: clients -> projects -> scenarios
        const clientsRes = await getClientFilterService({ showAll: false });
        if (ignore) return;
        const clientOpts = [
          { value: "All clients", label: "All clients" },
          ...clientsRes.data.map((c) => ({ value: c.id, userId: c?.user?.id, label: c?.user?.full_name })),
        ];
        setClientOptions(clientOpts);

        const projectsRes = await getProjectFilterListService({ search: "" });
        if (ignore) return;
        const projectOpts = [
          { value: "All projects", label: "All projects" },
          ...projectsRes.data.data.map((p) => ({ value: p.id, label: p.name })),
        ];
        setProjectOptions(projectOpts);

        const scenariosRes = await getScenariosListService();
        if (ignore) return;
        const scenarioOpts = [
          { value: "All scenarios", label: "All scenarios" },
          ...(scenariosRes.data.data || []).map((s: string) => ({ value: s, label: s })),
        ];
        setScenarioOptions(scenarioOpts);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  // When page loads, if URL contains encoded payload, decode and store in state
  useEffect(() => {
    if (payloadHandledRef.current) return;
    const params = new URLSearchParams(location.search);
    const payload = params.get("payload");
    if (!payload) return;
    try {
      const b64 = decodeURIComponent(payload);
      const raw = atob(b64);
      const bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
      const json = new TextDecoder().decode(bytes);
      const obj = JSON.parse(json);
      const c = obj?.c as string;
      const p = obj?.p as string;
      if (c && p) {
        setPrefill({ clientId: c, projectId: p });
      }
    } catch (e) {
      // Ignore malformed payloads
    } finally {
      payloadHandledRef.current = true;
    }
  }, [location.search]);

  // Open modal automatically once options are loaded and prefill is available
  useEffect(() => {
    if (!prefill) return;
    const clientsReady = clientOptions.length > 1; // includes 'All clients'
    const projectsReady = projectOptions.length > 1; // includes 'All projects'
    if (clientsReady && projectsReady) {
      setIsNewEmailOpen(true);
    }
  }, [prefill, clientOptions, projectOptions]);

  // Fetch emails when filters/page change
  useEffect(() => {
    const from = dateRange.startDate
      ? moment(dateRange.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")
      : undefined;
    const to = dateRange.endDate
      ? moment(dateRange.endDate, "YYYY-MM-DD").format("DD-MM-YYYY")
      : undefined;

    const payload: any = {
      page: currentPage,
      pageSize,
    };
    if (search) payload.search = search;
    if (projectFilter !== "All projects") payload.projectId = projectFilter;
    if (clientFilter !== "All clients") payload.clientId = clientFilter;
    if (scenarioFilter !== "All scenarios")
      payload.scenario = scenarioFilter.toLowerCase();
    if (statusFilter !== "All statuses")
      payload.status = statusFilter.toLowerCase();
    if (from) payload.fromDate = from;
    if (to) payload.toDate = to;

    // @ts-ignore thunk typing from slice
    dispatch(fetchEmails(payload));
  }, [
    dispatch,
    currentPage,
    pageSize,
    search,
    projectFilter,
    clientFilter,
    scenarioFilter,
    statusFilter,
    dateRange,
  ]);

  return (
    <AppLayout
      title="Emails"
      rightActions={
        <button
          onClick={handleAddProject}
          className="bg-[#73B5C5] hover:bg-[#5da7b8] text-white px-4 py-2 rounded-md text-sm flex items-center cursor-pointer gap-1"
        >
          New email
          <PlusIconSVG />
        </button>
      }
      contentClassName="min-h-[90%]"
    >
      <div className="p-4">
        <div className="bg-white rounded-lg">
          <EmailFilters
            search={search}
            onSearchChange={(v) => {
              setSearch(v);
              setCurrentPage(1);
            }}
            clientOptions={clientOptions}
            projectOptions={projectOptions}
            scenarioOptions={scenarioOptions}
            statusOptions={statusOptions}
            clientFilter={clientFilter}
            onClientChange={(v) => {
              setClientFilter(v);
              setCurrentPage(1);
            }}
            projectFilter={projectFilter}
            onProjectChange={(v) => {
              setProjectFilter(v);
              setCurrentPage(1);
            }}
            scenarioFilter={scenarioFilter}
            onScenarioChange={(v) => {
              setScenarioFilter(v);
              setCurrentPage(1);
            }}
            statusFilter={statusFilter}
            onStatusChange={(v) => {
              setStatusFilter(v);
              setCurrentPage(1);
            }}
            dateRange={dateRange}
            tempDateRange={tempDateRange}
            setDateRange={(v) => {
              setDateRange(v);
              setCurrentPage(1);
            }}
            setTempDateRange={setTempDateRange}
            onReset={() => {
              setClientFilter("All clients");
              setProjectFilter("All projects");
              setScenarioFilter("All scenarios");
              setStatusFilter("All statuses");
              setSearch("");
              setDateRange({ startDate: "", endDate: "" });
              setTempDateRange({ startDate: "", endDate: "" });
              setCurrentPage(1);
            }}
          />

          <EmailTable
            rows={rows as any}
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            totalItems={pagination.totalItems}
            onPrev={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
            onNext={() =>
              currentPage < pagination.totalPages && setCurrentPage(currentPage + 1)
            }
            onView={(row: any) => (row.status === "failed" ? handleResendFailedEmail(row.id) : handleViewEmail(row.id))}
          />
        </div>
      </div>
      <Modal
        isOpen={isNewEmailOpen}
        onClose={() => setIsNewEmailOpen(false)}
        title="New email"
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        <NewEmailForm
          clientOptions={clientOptions.filter((o) => o.value !== "All clients")}
          projectOptions={projectOptions.filter(
            (o) => o.value !== "All projects"
          )}
          scenarioOptions={scenarioOptions.filter(
            (o) => o.value !== "All scenarios"
          )}
          prefill={prefill}
          onSubmit={handleSendEmail}
          onCancel={() => setIsNewEmailOpen(false)}
          onViewEmails={handleViewEmailsFromForm}
        />
      </Modal>

      <Modal
        isOpen={isViewEmailOpen}
        onClose={() => setIsViewEmailOpen(false)}
        title="View email"
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        {viewEmailData ? <ViewEmailForm data={viewEmailData} /> : null}
      </Modal>

      <Modal
        isOpen={isResendOpen}
        onClose={() => setIsResendOpen(false)}
        title="Email failed to send"
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        {resendData ? (
          <ResendFailedEmailForm
            data={resendData}
            clientOptions={clientOptions.filter((o) => o.value !== "All clients")}
            projectOptions={projectOptions.filter((o) => o.value !== "All projects")}
            scenarioOptions={scenarioOptions.filter((o) => o.value !== "All scenarios")}
            emailId={resendEmailId || ""}
            onCancel={() => setIsResendOpen(false)}
            onSubmit={handleResendSuccessAndRefresh}
          />
        ) : null}
      </Modal>
    </AppLayout>
  );
}
